import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CloseIcon from '@mui/icons-material/Close';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { createAccessCode } from '../../services/SmartLocksService';

// const SEAM_SERVER_URL = process.env.REACT_APP_SEAM_SERVER_URL;

// Email server — matches your routes/email.js which mounts at /api/email
// and routes/tenantEmails.js which mounts at /api/tenant-emails
const EMAIL_SERVER_URL =
    process.env.REACT_APP_EMAIL_SERVER_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://ai-realtor.onrender.com');
// ─── helpers ──────────────────────────────────────────────────────────────────

const toLocalDatetimeValue = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const daysBetween = (startIso, endIso) => {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    return ms / (1000 * 60 * 60 * 24);
};

const fmtDate = (isoStr) =>
    isoStr
        ? new Date(isoStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
        : '—';

// ─── style constants ──────────────────────────────────────────────────────────

const typeCardBase = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
    p: 3,
    borderRadius: 3,
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    userSelect: 'none',
};

// ─── sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
    <Typography
        variant="caption"
        sx={{
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 0.5,
            display: 'block',
        }}
    >
        {children}
    </Typography>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const AccessCodeGeneratorModal = ({
    open,
    onClose,
    locks = [],
    tenantId,
    propertyId,
    onSuccess,
    ownerEmail,
    ownerName,
    agentDashboardUrl,
}) => {
    // ── view ──────────────────────────────────────────────────────────────────
    const [view, setView] = useState('choose');
    const [doneMessage, setDoneMessage] = useState('');
    const [donePending, setDonePending] = useState(false);

    // ── shared ────────────────────────────────────────────────────────────────
    // Use refs for form values to prevent re-render glitches on Select/DateInput
    const [selectedLockId, setSelectedLockId] = useState('');
    const [startDatetime, setStartDatetime] = useState('');
    const [endDatetime, setEndDatetime] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // ── permanent ─────────────────────────────────────────────────────────────
    const [leaseEndDate, setLeaseEndDate] = useState(null);
    const [leaseFetching, setLeaseFetching] = useState(false);

    // ── authorised ────────────────────────────────────────────────────────────
    const [authorisedUsers, setAuthorisedUsers] = useState([]);
    const [authorisedLoading, setAuthorisedLoading] = useState(false);

    // ── auth check ────────────────────────────────────────────────────────────
    const [isUserAuthorized, setIsUserAuthorized] = useState(true);
    const [authCheckLoading, setAuthCheckLoading] = useState(false);

    // Track whether modal was previously open to avoid double-reset
    const wasOpen = useRef(false);

    // ── check authorization ───────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !tenantId || !propertyId) return;
        setAuthCheckLoading(true);
        getDoc(doc(db, 'access_users', propertyId, 'tenant-emails', tenantId))
            .then((snap) => setIsUserAuthorized(snap.exists()))
            .catch(() => setIsUserAuthorized(true))
            .finally(() => setAuthCheckLoading(false));
    }, [open, tenantId, propertyId]);

    // ── reset ONLY on fresh open (not on every render while open) ────────────
    useEffect(() => {
        if (open && !wasOpen.current) {
            setView('choose');
            setSelectedLockId('');
            setStartDatetime('');
            setEndDatetime('');
            setError(null);
            setLoading(false);
            setDoneMessage('');
            setDonePending(false);
            setLeaseEndDate(null);
            setAuthorisedUsers([]);
        }
        wasOpen.current = open;
    }, [open]);

    // ── fetch lease end date when permanent view opens ────────────────────────
    useEffect(() => {
        if (view !== 'permanent' || !propertyId || !tenantId) return;
        setLeaseFetching(true);
        getDoc(doc(db, 'access_users', propertyId, 'tenant-emails', tenantId))
            .then((snap) => {
                if (snap.exists()) {
                    const raw = snap.data().leaseEndDate;
                    if (raw?.toDate) setLeaseEndDate(raw.toDate().toISOString());
                    else if (typeof raw === 'string') setLeaseEndDate(raw);
                    else setLeaseEndDate(null);
                } else {
                    setLeaseEndDate(null);
                }
            })
            .catch(() => setLeaseEndDate(null))
            .finally(() => setLeaseFetching(false));
    }, [view, propertyId, tenantId]);

    // ── fetch authorised users when authorised view opens ─────────────────────
    useEffect(() => {
        if (view !== 'authorised' || !propertyId) return;
        setAuthorisedLoading(true);
        getDocs(collection(db, 'access_users', propertyId, 'authorised_users'))
            .then((snap) =>
                setAuthorisedUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
            )
            .catch(() => setAuthorisedUsers([]))
            .finally(() => setAuthorisedLoading(false));
    }, [view, propertyId]);

    // ─────────────────────────────────────────────────────────────────────────
    // PATH A — PERMANENT CODE
    // ─────────────────────────────────────────────────────────────────────────
    const handlePermanentSubmit = async () => {
        setError(null);
        if (!isUserAuthorized) return setError('You are not authorized to create access codes.');
        if (!selectedLockId) return setError('Please select a lock.');
        if (!startDatetime || !endDatetime) return setError('Please fill in start and end date/time.');

        const start = new Date(`${startDatetime}T00:00:00`);
        const end = new Date(`${endDatetime}T23:59:59`);
        if (isNaN(start) || isNaN(end)) return setError('Invalid date/time values.');
        if (start >= end) return setError('Start must be before end date/time.');
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDateOnly = new Date(start);
        startDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly < today) {
            return setError('Start date cannot be in the past.');
        }

        if (leaseEndDate && end > new Date(leaseEndDate)) {
            return setError(`End date cannot exceed your lease end date (${fmtDate(leaseEndDate)}).`);
        }

        setLoading(true);
        try {
            const uid = localStorage.getItem('tenantUid') || tenantId;
            const codeName = `Perm-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
            const selectedLock = locks.find((l) => l.lockId === selectedLockId);
            const isOffline =
                selectedLock?.deviceType?.toLowerCase().includes('igloohome') ||
                selectedLock?.manufacturer?.toLowerCase().includes('igloo') ||
                false;

            const response = await createAccessCode(
                uid,
                selectedLockId,
                codeName,
                null,
                start.toISOString(),
                end.toISOString(),
                false,
                isOffline,
                tenantId,
                propertyId,
                {
                    isGuest: false,
                    isPermanentTenantCode: true,
                }
            );

            const code =
                response.accessCode?.access_code?.code ||
                response.accessCode?.code ||
                Math.floor(100000 + Math.random() * 900000).toString();

            setDoneMessage(`Permanent access code generated: ${code}`);
            setDonePending(false);
            setView('done');
            onSuccess?.(`Permanent code ${code} created successfully!`);
        } catch (err) {
            console.error('[AccessCodeModal] Permanent code error:', err);
            setError(
                err.message?.includes('Permission denied')
                    ? 'Permission denied. Please ensure you are logged in.'
                    : err.message || 'Failed to generate permanent access code.'
            );
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PATH B — AUTHORISED USERS CODE
    // ─────────────────────────────────────────────────────────────────────────
    const handleAuthorisedSubmit = async () => {
        setError(null);
        if (!isUserAuthorized) return setError('You are not authorized to create access codes.');
        if (!selectedLockId) return setError('Please select a lock.');
        if (!startDatetime || !endDatetime) return setError('Please fill in start and end date/time.');

        const start = new Date(`${startDatetime}T00:00:00`);
        const end = new Date(`${endDatetime}T23:59:59`);
        if (isNaN(start) || isNaN(end)) return setError('Invalid date/time values.');
        if (start >= end) return setError('Start must be before end date/time.');
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDateOnly = new Date(start);
        startDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly < today) {
            return setError('Start date cannot be in the past.');
        }

        // ── Lease date check for authorised codes too ─────────────────────────
        if (leaseEndDate && end > new Date(leaseEndDate)) {
            return setError(
                `End date cannot exceed your lease end date (${fmtDate(leaseEndDate)}).`
            );
        }

        if (authorisedUsers.length === 0) {
            return setError('No authorised users found for this property. Add users first.');
        }

        const days = daysBetween(start.toISOString(), end.toISOString());
        const requiresApproval = days > 7;

        setLoading(true);
        try {
            if (requiresApproval) {
                await submitForApproval(start, end, days);
            } else {
                await createAndEmailAuthorisedCode(start, end);
            }
        } catch (err) {
            console.error('[AccessCodeModal] Authorised code error:', err);
            setError(
                err.message?.includes('Permission denied')
                    ? 'Permission denied. Please check Firestore rules for authorised_user_code collection.'
                    : err.message || 'Failed to process authorised user code.'
            );
        } finally {
            setLoading(false);
        }
    };

    const submitForApproval = async (start, end, days) => {
        const pendingRef = doc(db, 'authorised_user_code', propertyId);
        await setDoc(
            pendingRef,
            {
                propertyId,
                tenantId,
                lockId: selectedLockId,
                startsAt: start.toISOString(),
                endsAt: end.toISOString(),
                durationDays: Math.round(days),
                status: 'pending_approval',
                requestedAt: serverTimestamp(),
                approvedAt: null,
                code: null,
                authorisedUsers: authorisedUsers.map((u) => ({
                    email: u.id,
                    name: u.name || '',
                })),
            },
            { merge: false }
        );

        const approvalUrl = `${agentDashboardUrl || window.location.origin + '/agent-dashboard'
            }?approveAuthCode=true&propertyId=${encodeURIComponent(
                propertyId
            )}&tenantId=${encodeURIComponent(tenantId)}&lockId=${encodeURIComponent(
                selectedLockId
            )}&startsAt=${encodeURIComponent(start.toISOString())}&endsAt=${encodeURIComponent(
                end.toISOString()
            )}`;

        // POST to email server — route is /send-auth-code-approval-request (no /api/ prefix
        // because routes/email.js is mounted directly, check your app.js mount path)
        await fetch(`${EMAIL_SERVER_URL}/send-auth-code-approval-request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ownerEmail,
                ownerName,
                tenantId,
                propertyId,
                lockName:
                    locks.find((l) => l.lockId === selectedLockId)?.deviceName || 'Smart Lock',
                durationDays: Math.round(days),
                startsAt: start.toISOString(),
                endsAt: end.toISOString(),
                authorisedUserCount: authorisedUsers.length,
                approvalUrl,
            }),
        });

        setDoneMessage(
            'Your request has been sent to the property manager for approval. You will be notified once approved.'
        );
        setDonePending(true);
        setView('done');
    };

    const createAndEmailAuthorisedCode = async (start, end) => {
        const uid = localStorage.getItem('tenantUid') || tenantId;
        const codeName = `Auth-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
        const selectedLock = locks.find((l) => l.lockId === selectedLockId);
        const isOffline =
            selectedLock?.deviceType?.toLowerCase().includes('igloohome') ||
            selectedLock?.manufacturer?.toLowerCase().includes('igloo') ||
            false;

        const response = await createAccessCode(
            uid,
            selectedLockId,
            codeName,
            null,
            start.toISOString(),
            end.toISOString(),
            false,
            isOffline,
            tenantId,
            propertyId,
            {
                isGuest: true,
                isPermanentTenantCode: false,
            }
        );

        const code =
            response.accessCode?.access_code?.code ||
            response.accessCode?.code ||
            Math.floor(100000 + Math.random() * 900000).toString();

        // Save to top-level authorised_user_code collection
        const authCodeRef = doc(db, 'authorised_user_code', propertyId);
        await setDoc(
            authCodeRef,
            {
                propertyId,
                tenantId,
                lockId: selectedLockId,
                lockName: selectedLock?.deviceName || 'Smart Lock',
                code,
                startsAt: start.toISOString(),
                endsAt: end.toISOString(),
                status: 'active',
                createdAt: serverTimestamp(),
                approvedAt: null,
                authorisedUsers: authorisedUsers.map((u) => ({
                    email: u.id,
                    name: u.name || '',
                })),
            },
            { merge: false }
        );

        // Send emails — route mounted at /send-auth-user-access-code in routes/email.js
        await fetch(`${EMAIL_SERVER_URL}/send-auth-user-access-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                recipients: authorisedUsers.map((u) => ({
                    email: u.id,
                    name: u.name || u.id,
                })),
                code,
                propertyId,
                propertyAddress: selectedLock?.deviceName || '',
                lockName: selectedLock?.deviceName || 'Smart Lock',
                startsAt: start.toISOString(),
                endsAt: end.toISOString(),
                tenantId,
            }),
        });

        setDoneMessage(
            `Access code ${code} generated and sent to ${authorisedUsers.length} authorised user(s).`
        );
        setDonePending(false);
        setView('done');
        onSuccess?.(`Authorised user code ${code} sent to ${authorisedUsers.length} user(s)!`);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // STABLE CHANGE HANDLERS — no stopPropagation, no inline functions in JSX
    // ─────────────────────────────────────────────────────────────────────────
    const handleLockChange = useCallback((e) => {
        setSelectedLockId(e.target.value);
    }, []);

    const handleStartChange = useCallback((e) => {
        setStartDatetime(e.target.value);
    }, []);

    const handleEndChange = useCallback((e) => {
        setEndDatetime(e.target.value);
    }, []);

    const handleGoBack = useCallback(() => {
        setView('choose');
        setError(null);
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER HELPERS — defined as stable components outside render cycle
    // ─────────────────────────────────────────────────────────────────────────
    const durationDays =
        startDatetime && endDatetime && new Date(startDatetime) < new Date(endDatetime)
            ? Math.round(daysBetween(new Date(startDatetime).toISOString(), new Date(endDatetime).toISOString()))
            : null;

    const requiresApproval = durationDays !== null && durationDays > 7;

    // ── Choose view ───────────────────────────────────────────────────────────
    if (authCheckLoading && view === 'choose') {
        return (
            <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 4 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Verifying authorization…
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    const titles = {
        choose: 'Generate Access Code',
        permanent: 'Permanent Tenant Code',
        authorised: 'Authorised Users Code',
        done: '',
    };

    const primaryLabel =
        view === 'permanent'
            ? loading
                ? 'Generating…'
                : 'Generate Permanent Code'
            : view === 'authorised'
                ? loading
                    ? 'Processing…'
                    : requiresApproval
                        ? 'Request Manager Approval'
                        : 'Generate & Send to All Users'
                : view === 'done'
                    ? 'Close'
                    : null;

    const handlePrimaryAction = () => {
        if (view === 'permanent') handlePermanentSubmit();
        else if (view === 'authorised') handleAuthorisedSubmit();
        else if (view === 'done') onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                // Never close on backdrop click while form is active — prevents glitch
                if (loading) return;
                if (reason === 'backdropClick' && view !== 'choose' && view !== 'done') return;
                onClose();
            }}
            maxWidth="sm"
            fullWidth
            // CRITICAL: disablePortal + keepMounted prevent MUI from
            // unmounting Select/DateInput popups mid-interaction
            keepMounted={false}
            disableEscapeKeyDown={loading || (view !== 'choose' && view !== 'done')}
            PaperProps={{ sx: { borderRadius: 3 } }}
            // Prevent dialog from stealing focus from open Select menus
            disableEnforceFocus
        >
            {/* Header */}
            <DialogTitle
                sx={{ pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {view !== 'choose' && view !== 'done' && (
                        <Tooltip title="Back">
                            <IconButton
                                size="small"
                                onClick={handleGoBack}
                                disabled={loading}
                                sx={{ mr: 0.5 }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Typography fontWeight={700} fontSize={17}>
                        {titles[view]}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={loading}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* Step indicator */}
            {(view === 'permanent' || view === 'authorised') && (
                <Box sx={{ px: 3, pb: 0.5 }}>
                    <Stepper
                        activeStep={1}
                        alternativeLabel
                        sx={{ '& .MuiStepLabel-label': { fontSize: 11 } }}
                    >
                        <Step completed>
                            <StepLabel>Choose Type</StepLabel>
                        </Step>
                        <Step active>
                            <StepLabel>Configure</StepLabel>
                        </Step>
                        <Step>
                            <StepLabel>Done</StepLabel>
                        </Step>
                    </Stepper>
                    <Divider sx={{ mt: 1.5 }} />
                </Box>
            )}

            <DialogContent sx={{ pt: 2 }}>
                {/* ── CHOOSE ── */}
                {view === 'choose' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        {!isUserAuthorized ? (
                            <Alert severity="error">
                                You are not authorized to create access codes for this property.
                                Please contact your property manager.
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    Choose the type of access code you want to generate:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box
                                        onClick={() => setView('permanent')}
                                        sx={{
                                            ...typeCardBase,
                                            borderColor: '#4caf50',
                                            backgroundColor: '#f1f8f1',
                                            '&:hover': {
                                                backgroundColor: '#e8f5e9',
                                                boxShadow: '0 4px 20px rgba(76,175,80,0.18)',
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                bgcolor: '#4caf50',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <LockIcon sx={{ color: '#fff', fontSize: 24 }} />
                                        </Box>
                                        <Typography fontWeight={700} fontSize={15} textAlign="center">
                                            Permanent Tenant Code
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            textAlign="center"
                                            lineHeight={1.5}
                                        >
                                            A personal code tied to your tenancy. Cannot exceed your
                                            lease end date.
                                        </Typography>
                                        <Chip
                                            label="For yourself"
                                            size="small"
                                            sx={{
                                                bgcolor: '#c8e6c9',
                                                color: '#2e7d32',
                                                fontWeight: 700,
                                                fontSize: 10,
                                            }}
                                        />
                                    </Box>

                                    <Box
                                        onClick={() => setView('authorised')}
                                        sx={{
                                            ...typeCardBase,
                                            borderColor: '#2196f3',
                                            backgroundColor: '#f0f6ff',
                                            '&:hover': {
                                                backgroundColor: '#e3f2fd',
                                                boxShadow: '0 4px 20px rgba(33,150,243,0.18)',
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                bgcolor: '#2196f3',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <GroupIcon sx={{ color: '#fff', fontSize: 24 }} />
                                        </Box>
                                        <Typography fontWeight={700} fontSize={15} textAlign="center">
                                            Authorised Users Code
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            textAlign="center"
                                            lineHeight={1.5}
                                        >
                                            A shared code sent to all authorised users. Requests over
                                            7 days need manager approval.
                                        </Typography>
                                        <Chip
                                            label="For your group"
                                            size="small"
                                            sx={{
                                                bgcolor: '#bbdefb',
                                                color: '#1565c0',
                                                fontWeight: 700,
                                                fontSize: 10,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                {/* ── PERMANENT ── */}
                {view === 'permanent' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        {/* Lease info */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid #c8e6c9',
                                bgcolor: '#f1f8f1',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <LockIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            {leaseFetching ? (
                                <CircularProgress size={14} />
                            ) : (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Lease End Date
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        color={leaseEndDate ? '#2e7d32' : '#d32f2f'}
                                    >
                                        {leaseEndDate
                                            ? fmtDate(leaseEndDate)
                                            : 'Not found — contact your manager'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Lock selector */}
                        <Box>
                            <SectionLabel>Lock</SectionLabel>
                            <FormControl fullWidth size="small" disabled={loading}>
                                <InputLabel>Select Smart Lock</InputLabel>
                                <Select
                                    value={selectedLockId}
                                    label="Select Smart Lock"
                                    onChange={handleLockChange}
                                >
                                    {locks.length > 0 ? (
                                        locks.map((lock) => (
                                            <MenuItem key={lock.lockId} value={lock.lockId}>
                                                {lock.deviceName ||
                                                    `Lock ${lock.lockId.slice(0, 8)}…`}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled value="">
                                            No locks available
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Date fields */}
                        <Box>
                            <SectionLabel>Access Period</SectionLabel>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Start"
                                    type="date"
                                    fullWidth
                                    size="small"
                                    value={startDatetime}
                                    onChange={handleStartChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: new Date().toISOString().split('T')[0],
                                    }}
                                    disabled={loading}
                                />
                                <TextField
                                    label="End"
                                    type="date"
                                    fullWidth
                                    size="small"
                                    value={endDatetime}
                                    onChange={handleEndChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min:
                                            startDatetime ||
                                            new Date().toISOString().split('T')[0],
                                        max: leaseEndDate
                                            ? new Date(leaseEndDate).toISOString().split('T')[0]
                                            : undefined,
                                    }}
                                    disabled={loading}
                                />
                            </Box>
                            {durationDays !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <ScheduleIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Duration: <strong>{durationDays} day(s)</strong>
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}

                {/* ── AUTHORISED ── */}
                {view === 'authorised' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        {/* Users list */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid #bbdefb',
                                bgcolor: '#f0f6ff',
                            }}
                        >
                            <Typography
                                variant="caption"
                                fontWeight={700}
                                color="#1565c0"
                                sx={{ display: 'block', mb: 1 }}
                            >
                                Code will be sent to{' '}
                                {authorisedLoading ? '…' : authorisedUsers.length} authorised user(s)
                            </Typography>
                            {authorisedLoading ? (
                                <CircularProgress size={14} />
                            ) : authorisedUsers.length === 0 ? (
                                <Typography variant="caption" color="error">
                                    No authorised users found. Add users in the Property card first.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {authorisedUsers.map((u) => (
                                        <Chip
                                            key={u.id}
                                            label={u.email || u.id}
                                            size="small"
                                            sx={{
                                                fontSize: 11,
                                                height: 22,
                                                bgcolor: '#dbeafe',
                                                color: '#1e40af',
                                                fontWeight: 600,
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* >7 day approval notice */}
                        <Box
                            sx={{
                                p: 1.75,
                                borderRadius: 2,
                                border: '1px solid #ffe0b2',
                                bgcolor: '#fff8f0',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <HourglassTopIcon
                                    sx={{ fontSize: 16, color: '#f57c00', mt: 0.2 }}
                                />
                                <Typography variant="caption" color="#e65100" lineHeight={1.6}>
                                    <strong>Approval required for codes &gt; 7 days.</strong> The
                                    property manager will receive an email to approve. The code is
                                    only created after approval.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Lock selector */}
                        <Box>
                            <SectionLabel>Lock</SectionLabel>
                            <FormControl fullWidth size="small" disabled={loading}>
                                <InputLabel>Select Smart Lock</InputLabel>
                                <Select
                                    value={selectedLockId}
                                    label="Select Smart Lock"
                                    onChange={handleLockChange}
                                >
                                    {locks.length > 0 ? (
                                        locks.map((lock) => (
                                            <MenuItem key={lock.lockId} value={lock.lockId}>
                                                {lock.deviceName ||
                                                    `Lock ${lock.lockId.slice(0, 8)}…`}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled value="">
                                            No locks available
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Date fields */}
                        <Box>
                            <SectionLabel>Access Period</SectionLabel>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Start"
                                    type="datetime-local"
                                    fullWidth
                                    size="small"
                                    value={startDatetime}
                                    onChange={handleStartChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: toLocalDatetimeValue(new Date().toISOString()),
                                    }}
                                    disabled={loading}
                                />
                                <TextField
                                    label="End"
                                    type="datetime-local"
                                    fullWidth
                                    size="small"
                                    value={endDatetime}
                                    onChange={handleEndChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min:
                                            startDatetime ||
                                            toLocalDatetimeValue(new Date().toISOString()),
                                    }}
                                    disabled={loading}
                                />
                            </Box>
                            {durationDays !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <ScheduleIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Duration: <strong>{durationDays} day(s)</strong>
                                        {requiresApproval && (
                                            <Chip
                                                label="Requires Manager Approval"
                                                size="small"
                                                icon={<HourglassTopIcon />}
                                                sx={{
                                                    ml: 1,
                                                    fontSize: 10,
                                                    height: 20,
                                                    bgcolor: '#fff3e0',
                                                    color: '#e65100',
                                                    border: '1px solid #ffcc80',
                                                }}
                                            />
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}

                {/* ── DONE ── */}
                {view === 'done' && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            py: 3,
                            textAlign: 'center',
                        }}
                    >
                        {donePending ? (
                            <HourglassTopIcon sx={{ fontSize: 56, color: '#ff9800' }} />
                        ) : (
                            <CheckCircleIcon sx={{ fontSize: 56, color: '#4caf50' }} />
                        )}
                        <Typography variant="h6" fontWeight={700}>
                            {donePending ? 'Approval Request Sent' : 'Code Generated!'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                            {doneMessage}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {view !== 'choose' && (
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    {view !== 'done' && (
                        <Button
                            onClick={handleGoBack}
                            disabled={loading}
                            sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handlePrimaryAction}
                        disabled={loading}
                        startIcon={
                            loading ? <CircularProgress size={16} color="inherit" /> : null
                        }
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 3,
                            bgcolor:
                                view === 'permanent'
                                    ? '#4caf50'
                                    : view === 'authorised'
                                        ? '#2196f3'
                                        : '#4caf50',
                            '&:hover': {
                                bgcolor:
                                    view === 'permanent'
                                        ? '#43a047'
                                        : view === 'authorised'
                                            ? '#1e88e5'
                                            : '#43a047',
                            },
                        }}
                    >
                        {primaryLabel}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default AccessCodeGeneratorModal;